import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { LineChart, LinePlot, MarkPlot } from '@mui/x-charts/LineChart';
import { BarChart, BarPlot } from '@mui/x-charts/BarChart';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsLegend } from '@mui/x-charts/ChartsLegend';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';

export type ChartType = 'line' | 'bar';

export interface ChartDataPoint {
    x: string | number;
    y: number;
}

export interface ChartSeries {
    id: string;
    label: string;
    data: ChartDataPoint[] | number[];
    color?: string;
    type?: 'line' | 'bar'; // Optional type for mixed charts
    stack?: string; // Optional stack group for stacked bars
    yAxisId?: string; // Optional Y-axis ID for dual axes
}

export interface CommonChartProps {
    type: ChartType;
    title?: string;
    series: ChartSeries[];
    width?: number;
    height?: number;
    xAxisLabel?: string;
    yAxisLabel?: string;
    rightYAxisLabel?: string; // Label for right Y-axis
    xAxisData?: (string | number)[];
    showGrid?: boolean;
    showLegend?: boolean;
    showTooltip?: boolean;
    margin?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
    };
    sx?: object;
}

const CommonChart: React.FC<CommonChartProps> = ({
    type,
    title,
    series,
    width,
    height = 400,
    xAxisLabel,
    yAxisLabel,
    rightYAxisLabel,
    xAxisData,
    showGrid = true,
    showLegend = true,
    showTooltip = true,
    margin = { top: 50, right: 30, bottom: 50, left: 60 },
    sx = {}
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [chartWidth, setChartWidth] = React.useState<number>(width || 600);

    // Use ResizeObserver to make chart responsive when width is not provided
    React.useEffect(() => {
        if (width) return; // If width is explicitly provided, don't observe

        const observeTarget = containerRef.current;
        if (!observeTarget) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (entries[0]) {
                const containerWidth = entries[0].contentRect.width;
                setChartWidth(containerWidth > 0 ? containerWidth : 600);
            }
        });

        resizeObserver.observe(observeTarget);
        return () => resizeObserver.disconnect();
    }, [width]);

    // Transform series data for MUI X-Charts v7 format
    const transformedSeries = series.map(serie => {
        let data: number[];
        
        if (Array.isArray(serie.data) && serie.data.length > 0) {
            // Check if data contains objects with x, y properties
            if (typeof serie.data[0] === 'object' && 'x' in serie.data[0] && 'y' in serie.data[0]) {
                data = (serie.data as ChartDataPoint[]).map(point => point.y);
            } else {
                data = serie.data as number[];
            }
        } else {
            data = serie.data as number[];
        }

        return {
            id: serie.id,
            label: serie.label,
            data: data,
            color: serie.color
        };
    });

    // Extract x-axis data if not provided
    const chartXAxisData = xAxisData || (() => {
        if (series.length > 0 && Array.isArray(series[0].data) && series[0].data.length > 0) {
            const firstDataPoint = series[0].data[0];
            if (typeof firstDataPoint === 'object' && 'x' in firstDataPoint) {
                return (series[0].data as ChartDataPoint[]).map(point => point.x);
            }
        }
        return [];
    })();

    const renderChart = () => {
        // Check if we have mixed chart types
        const hasMixedTypes = series.some(s => s.type) && series.some(s => s.type === 'bar') && series.some(s => s.type === 'line');

        if (hasMixedTypes) {
            // Render mixed chart using ResponsiveChartContainer
            const barSeries = transformedSeries.filter((s, idx) => series[idx].type === 'bar');
            const lineSeries = transformedSeries.filter((s, idx) => series[idx].type === 'line');

            // Build Y-axes configuration
            const yAxes: any[] = [
                {
                    id: 'left-axis',
                    ...(yAxisLabel && { label: yAxisLabel })
                }
            ];

            if (rightYAxisLabel) {
                yAxes.push({
                    id: 'right-axis',
                    label: rightYAxisLabel
                });
            }

            return (
                <ResponsiveChartContainer
                    width={chartWidth}
                    height={height}
                    series={[
                        ...barSeries.map((s, idx) => ({ 
                            ...s, 
                            type: 'bar' as const,
                            stack: series.filter(ser => ser.type === 'bar')[idx].stack,
                            yAxisKey: 'left-axis'
                        })),
                        ...lineSeries.map((s, idx) => ({
                            ...s,
                            type: 'line' as const,
                            yAxisKey: series.filter(ser => ser.type === 'line')[idx].yAxisId || 'left-axis'
                        }))
                    ]}
                    xAxis={[{
                        data: chartXAxisData,
                        scaleType: 'band' as const,
                        id: 'x-axis',
                        ...(xAxisLabel && { label: xAxisLabel })
                    }]}
                    yAxis={yAxes}
                    margin={margin}
                >
                    {showGrid && <ChartsGrid vertical horizontal />}
                    <BarPlot />
                    <LinePlot />
                    <MarkPlot />
                    <ChartsXAxis />
                    <ChartsYAxis axisId="left-axis" />
                    {rightYAxisLabel && <ChartsYAxis axisId="right-axis" position="right" />}
                    {showTooltip && <ChartsTooltip />}
                    {showLegend && <ChartsLegend />}
                </ResponsiveChartContainer>
            );
        }

        // Base chart props common to both chart types
        const baseProps = {
            width: chartWidth,
            height,
            series: transformedSeries,
            margin,
            ...(showGrid && {
                grid: { vertical: true, horizontal: true }
            }),
            ...(yAxisLabel && {
                yAxis: [{ label: yAxisLabel }]
            }),
            ...(showLegend && {
                legend: { hidden: false }
            })
        };

        // Configure x-axis based on chart type
        const xAxisConfig = chartXAxisData.length > 0 ? {
            xAxis: [{
                data: chartXAxisData,
                scaleType: type === 'bar' ? 'band' as const : 'point' as const,
                ...(xAxisLabel && { label: xAxisLabel })
            }]
        } : {};

        const chartProps = {
            ...baseProps,
            ...xAxisConfig
        };

        switch (type) {
            case 'line':
                return <LineChart {...chartProps} />;
            case 'bar':
                return <BarChart {...chartProps} />;
            default:
                return (
                    <Typography color="error" align="center">
                        Unsupported chart type: {type}
                    </Typography>
                );
        }
    };

    return (
        <Card sx={{ ...sx }} ref={containerRef}>
            <CardContent>
                {title && (
                    <Typography variant="h6" component="h2" gutterBottom align="center">
                        {title}
                    </Typography>
                )}
                <Box display="flex" justifyContent="center" alignItems="center">
                    {renderChart()}
                </Box>
            </CardContent>
        </Card>
    );
};

export default CommonChart;